/* ===========================================================
   Calculadora de ROAS de equilíbrio (/calculadora/roas-de-equilibrio).
   Ferramenta pública gratuita — ranqueia para "calculadora de ROAS",
   é linkável (linkbait) e funila para a demo. 100% client-side, reusa as
   funções PURAS de domain/roas.ts (regra: cálculo de dinheiro vive em domain).
   Renderiza com valores-exemplo no SSG, então o crawler vê um resultado.
   =========================================================== */
import { useRef, useState } from "react";
import { useSeo } from "../../seo/use-seo";
import { titulo } from "../../seo/site";
import { JsonLd, breadcrumbLd, webAppLd } from "../../seo/jsonld";
import { breakevenRoas, contributionMarginPct } from "../../domain/roas";
import { fmt } from "../../domain/format";
import { useContentTracking } from "../../analytics/content-tracking";
import { calculadoraUsada } from "../../analytics/events";
import { ConteudoShell, CtaDemo } from "./shell";

const HOME = { nome: "Início", path: "/" };
const BLOG = { nome: "Blog", path: "/blog" };
const CALC = { nome: "Calculadora de ROAS de equilíbrio", path: "/calculadora/roas-de-equilibrio" };

/** "40" ou "40,5" → 40 / 40.5; vazio/inválido → 0. */
function num(s: string): number {
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function Campo({
  label,
  valor,
  onChange,
  sufixo = "%",
}: {
  label: string;
  valor: string;
  onChange: (v: string) => void;
  sufixo?: string;
}) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
      <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
        <input
          className="input"
          inputMode="decimal"
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          style={{
            padding: "10px 38px 10px 12px",
            borderRadius: 10,
            fontSize: 16,
          }}
        />
        <span style={{ position: "absolute", right: 12, color: "var(--ink-soft)", fontSize: 14 }}>
          {sufixo}
        </span>
      </span>
    </label>
  );
}

export function CalculadoraRoas() {
  useSeo({
    title: titulo("Calculadora de ROAS de equilíbrio (breakeven) grátis"),
    description:
      "Calcule em segundos o ROAS mínimo para sua campanha parar de dar prejuízo. Informe CMV, frete e imposto e veja sua margem de contribuição e o ROAS de equilíbrio.",
    path: "/calculadora/roas-de-equilibrio",
  });
  useContentTracking({ tipo: "calculadora", titulo: "ROAS de equilíbrio" });

  // "Calculadora Usada" 1x quando o visitante mexe em qualquer campo.
  const usou = useRef(false);
  const marcarUso = () => {
    if (!usou.current) {
      usou.current = true;
      calculadoraUsada({ calc: "roas-de-equilibrio" });
    }
  };

  const [cmv, setCmv] = useState("40");
  const [frete, setFrete] = useState("8");
  const [imposto, setImposto] = useState("6");

  const soma = num(cmv) + num(frete) + num(imposto); // custos variáveis, % da receita
  const margemPct = contributionMarginPct(100, soma); // fração ou null
  const breakeven = breakevenRoas(100, soma); // number | null

  return (
    <ConteudoShell breadcrumb={[HOME, BLOG, CALC]}>
      <JsonLd data={breadcrumbLd([HOME, BLOG, CALC])} />
      <JsonLd
        data={webAppLd({
          nome: "Calculadora de ROAS de equilíbrio",
          descricao:
            "Calcule o ROAS de equilíbrio (breakeven) a partir do CMV, frete e imposto da sua loja.",
          path: "/calculadora/roas-de-equilibrio",
        })}
      />
      <h1 id="conteudo-h1">Calculadora de ROAS de equilíbrio</h1>
      <p className="legal-data">
        O ROAS de equilíbrio é o mínimo para a campanha parar de consumir caixa. Informe seus custos
        variáveis (como % da receita) e veja o seu.
      </p>

      <div
        className="card"
        style={{ padding: 22, display: "grid", gap: 16, gridTemplateColumns: "1fr", marginTop: 8 }}
      >
        <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(3, 1fr)" }}>
          <Campo
            label="CMV"
            valor={cmv}
            onChange={(v) => {
              marcarUso();
              setCmv(v);
            }}
          />
          <Campo
            label="Frete"
            valor={frete}
            onChange={(v) => {
              marcarUso();
              setFrete(v);
            }}
          />
          <Campo
            label="Imposto"
            valor={imposto}
            onChange={(v) => {
              marcarUso();
              setImposto(v);
            }}
          />
        </div>

        <div
          style={{
            display: "grid",
            gap: 14,
            gridTemplateColumns: "1fr 1fr",
            borderTop: "1px solid var(--linha)",
            paddingTop: 16,
          }}
        >
          <div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>Margem de contribuição</div>
            <strong style={{ fontSize: 28, color: "var(--ink)" }}>
              {margemPct == null ? "—" : fmt.pct(margemPct, { dec: 1 })}
            </strong>
          </div>
          <div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>ROAS de equilíbrio</div>
            <strong style={{ fontSize: 28, color: "var(--mint)" }}>
              {breakeven == null ? "—" : fmt.roas(breakeven)}
            </strong>
          </div>
        </div>

        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--ink-soft)" }}>
          {breakeven == null
            ? "Com esses custos não há ROAS de equilíbrio: cada venda já sai no prejuízo antes da mídia. Revise CMV, frete e imposto."
            : `Toda campanha rodando abaixo de ROAS ${fmt.roas(breakeven)} está dando prejuízo — por mais que o gerenciador mostre ROAS positivo. Acima dele, vale escalar.`}
        </p>
      </div>

      <h2>Como o cálculo funciona</h2>
      <p>
        Some seus custos variáveis como percentual da receita (CMV + frete + imposto). O que sobra é
        a <strong>margem de contribuição</strong>. O <strong>ROAS de equilíbrio</strong> é{" "}
        <strong>1 ÷ margem de contribuição</strong>. Margem de 40% → ROAS de equilíbrio 2,5.
      </p>

      <CtaDemo
        titulo="Quer o ROAS de equilíbrio por campanha, automático?"
        sub="O Lucraa+ calcula isso com os custos reais de cada produto da sua Nuvemshop e compara, campanha a campanha, com o ROAS real — apontando o que escalar e o que pausar."
      />
    </ConteudoShell>
  );
}
